import type { LegendListRef } from '@legendapp/list/react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useRef, useState } from 'react';
import { Platform, type ScrollViewProps } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { useQuery, useSubscription } from 'urql';
import Paginator from '@/components/Custom/Paginator';
import Placeholder from '@/components/Custom/Placeholder';
import SearchBar from '@/components/Custom/SearchBar';
import type { PeopleFilter } from '@/components/People/PeopleFilterSelector';
import PeopleListItem from '@/components/People/PeopleListItem';
import PeopleRowActions from '@/components/People/PeopleRowActions';
import PeopleToolbar from '@/components/People/PeopleToolbar';
import ActionButton from '@/components/UI/ActionButton';
import Badge from '@/components/UI/Badge';
import Checkbox from '@/components/UI/Checkbox';
import Section from '@/components/UI/Section';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import List from '@/components/Unistyles/List';
import { graphql } from '@/gql/graphql';
import useDebounce from '@/hooks/useDebounce';
import usePaginatedList from '@/hooks/usePaginatedList';
import {
  useSelectionClear,
  useSelectionSelectedIds,
  useSelectionStore,
  useSelectionToggle,
} from '@/hooks/useSelection';
import useUserId from '@/hooks/useUserId';
import { getOrganizationMemberAttributeColumnId } from '@/screens/OrganizationDetailMembers/OrganizationDetailMembers.utils';
import OrganizationMemberBadgeAssignmentMenu from '@/screens/OrganizationDetailMembers/OrganizationMemberBadgeAssignmentMenu';
import OrganizationMemberFilterMenu from '@/screens/OrganizationDetailMembers/OrganizationMemberFilterMenu';
import { getTeamMemberSettingsActions } from '@/screens/TeamPersonUpdate/TeamPersonUpdate.utils';
import { useMemberFilterValues } from '@/stores/useMemberFilterStore';
import useRootStore from '@/stores/useRootStore';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';
import {
  getTeamMembersPeopleWhere,
  TEAM_MEMBER_TABLE_PREVIEW_LIMIT,
  type TeamMemberRow,
  toTeamMemberRow,
} from './TeamDetailMember.utils';

const PeopleList = withUnistyles(List<TeamMemberRow>, (theme) => ({
  contentPadding: {
    paddingTop: theme.space('md'),
    paddingBottom: theme.space('xl'),
  },
}));

const PEOPLE_PAGE_LIMIT = 25;
const BADGE_FILTER_LIMIT = 250;
const NO_SELECTED_BADGE_ID = '00000000-0000-0000-0000-000000000000';
const scrollInsetProps: Pick<
  ScrollViewProps,
  'contentInsetAdjustmentBehavior'
> =
  Platform.OS === 'ios' ? { contentInsetAdjustmentBehavior: 'automatic' } : {};

export type TeamDetailMemberPathname =
  | '/chat/team/member/[personId]'
  | '/event/team/member/[personId]'
  | '/organization/event/team/member/[personId]'
  | '/organization/team/member/[personId]'
  | '/team/member/[personId]';

const GetTeamMembers = graphql(/* GraphQL */ `
  query GetTeamMembers(
    $teamId: uuid!
    $peopleWhere: person_team_bool_exp!
    $peopleLimit: Int!
    $peopleOffset: Int!
    $selectedBadgeId: uuid!
    $hasSelectedBadge: Boolean!
    $badgeFilterLimit: Int!
    $memberPreviewLimit: Int!
  ) {
    team_by_pk(id: $teamId) {
      id
      title
      role
      permissions
      on_team
      is_locked
    }
    relevant_badge(
      args: { team_id: $teamId }
      limit: $badgeFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      color
      badge_assignees_aggregate(where: { team_id: { _eq: $teamId } }) {
        aggregate {
          count
        }
      }
      badge_attributes(order_by: { order: asc }) {
        id
        title
        is_required
      }
    }
    person_team(
      where: $peopleWhere
      limit: $peopleLimit
      offset: $peopleOffset
      order_by: { person: { name: asc } }
    ) {
      member_type
      person_id
      team_id
      role
      person {
        id
        name
        avatar_id
        color
        is_managed
        badge_assignments(
          where: {
            team_id: { _eq: $teamId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: $memberPreviewLimit
        ) {
          id
          preview_value
          badge {
            id
            title
            color
          }
        }
        selected_badge_assignments: badge_assignments(
          where: {
            team_id: { _eq: $teamId }
            badge_id: { _eq: $selectedBadgeId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: 1
        ) @include(if: $hasSelectedBadge) {
          id
          badge_assignee_attributes(
            order_by: { badge_attribute: { order: asc } }
          ) {
            id
            badge_attribute_id
            value
            formatted_value
          }
        }
      }
    }
    person_team_aggregate(where: $peopleWhere) {
      aggregate {
        count
      }
    }
  }
`);

const WatchTeamMembers = graphql(/* GraphQL */ `
  subscription WatchTeamMembers(
    $teamId: uuid!
    $peopleWhere: person_team_bool_exp!
    $peopleLimit: Int!
    $peopleOffset: Int!
    $selectedBadgeId: uuid!
    $hasSelectedBadge: Boolean!
    $memberPreviewLimit: Int!
  ) {
    person_team(
      where: $peopleWhere
      limit: $peopleLimit
      offset: $peopleOffset
      order_by: { person: { name: asc } }
    ) {
      member_type
      person_id
      team_id
      role
      person {
        id
        name
        avatar_id
        color
        is_managed
        badge_assignments(
          where: {
            team_id: { _eq: $teamId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: $memberPreviewLimit
        ) {
          id
          preview_value
          badge {
            id
            title
            color
          }
        }
        selected_badge_assignments: badge_assignments(
          where: {
            team_id: { _eq: $teamId }
            badge_id: { _eq: $selectedBadgeId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: 1
        ) @include(if: $hasSelectedBadge) {
          id
          badge_assignee_attributes(
            order_by: { badge_attribute: { order: asc } }
          ) {
            id
            badge_attribute_id
            value
            formatted_value
          }
        }
      }
    }
  }
`);

export default function TeamDetailMember({
  memberPathname,
}: {
  memberPathname: TeamDetailMemberPathname;
}) {
  const rootTeamId = useRootStore((state) => state.teamId);
  const localParams = useLocalSearchParams<{
    teamId?: string;
  }>();
  const { teamId: teamIdParam } = localParams;
  const teamId = teamIdParam || rootTeamId;
  const filterKey = `team-members:${teamId ?? ''}`;
  const memberFilter = useMemberFilterValues(filterKey);

  if (!teamId) {
    return <Redirect href="/event" />;
  }

  return (
    <TeamDetailMemberContent
      filterKey={filterKey}
      memberPathname={memberPathname}
      peopleFilter={memberFilter.memberType}
      selectedBadgeIds={memberFilter.badgeIds}
      teamId={teamId}
    />
  );
}

function TeamDetailMemberContent({
  filterKey,
  memberPathname,
  peopleFilter,
  selectedBadgeIds,
  teamId,
}: {
  filterKey: string;
  memberPathname: TeamDetailMemberPathname;
  peopleFilter: PeopleFilter;
  selectedBadgeIds: string[];
  teamId: string;
}) {
  const { navigate, push } = useRouter();
  const userId = useUserId();

  const listRef = useRef<LegendListRef>(null);
  const { value, debouncedValue, onChangeText, reset } = useDebounce(
    DEFAULT_SEARCH_MINIMUM,
  );
  const paginated = usePaginatedList({
    limit: PEOPLE_PAGE_LIMIT,
    listRef,
  });
  const peopleWhere = getTeamMembersPeopleWhere({
    badgeIds: selectedBadgeIds,
    memberType: peopleFilter,
    searchTerm: debouncedValue,
    teamId,
  });
  const selectedBadgeQueryId =
    selectedBadgeIds.length === 1
      ? (selectedBadgeIds[0] ?? NO_SELECTED_BADGE_ID)
      : NO_SELECTED_BADGE_ID;
  const hasSelectedBadge = selectedBadgeIds.length === 1;
  const [{ data, fetching }] = useQuery({
    query: GetTeamMembers,
    variables: {
      teamId,
      peopleWhere,
      peopleLimit: paginated.limit,
      peopleOffset: paginated.offset,
      selectedBadgeId: selectedBadgeQueryId,
      hasSelectedBadge,
      badgeFilterLimit: BADGE_FILTER_LIMIT,
      memberPreviewLimit: TEAM_MEMBER_TABLE_PREVIEW_LIMIT,
    },
    requestPolicy: 'cache-and-network',
  });
  useSubscription({
    query: WatchTeamMembers,
    variables: {
      teamId,
      peopleWhere,
      peopleLimit: paginated.limit,
      peopleOffset: paginated.offset,
      selectedBadgeId: selectedBadgeQueryId,
      hasSelectedBadge,
      memberPreviewLimit: TEAM_MEMBER_TABLE_PREVIEW_LIMIT,
    },
  });

  const team = data?.team_by_pk;
  const rows = (data?.person_team ?? []).map(toTeamMemberRow);
  const visiblePersonIds = rows.map((row) => row.personId);
  const personSelectionStore = useSelectionStore<string>();
  const clearSelectedPeople = useSelectionClear(personSelectionStore);
  const selectedPersonIds = useSelectionSelectedIds(personSelectionStore);
  const toggleSelectedPerson = useSelectionToggle(personSelectionStore);
  const [personSelectionMode, setPersonSelectionMode] = useState(false);
  const isPersonSelectionMode =
    personSelectionMode || selectedPersonIds.length > 0;
  const peopleCount = data?.person_team_aggregate?.aggregate?.count ?? 0;
  const selectedBadge = hasSelectedBadge
    ? data?.relevant_badge?.find((badge) => badge.id === selectedBadgeQueryId)
    : null;
  const selectedBadgeAttributes = selectedBadge?.badge_attributes ?? [];
  const canAssign = Boolean(team?.permissions?.assign_badge);
  const canInvite = Boolean(
    team?.permissions?.create_invite && !team?.is_locked,
  );
  const peopleLabel =
    peopleFilter === 'all'
      ? 'People'
      : peopleFilter === 'participant'
        ? 'Participants'
        : 'Non Participants';
  const onPressRow = (row: TeamMemberRow) => {
    if (isPersonSelectionMode) {
      toggleSelectedPerson(
        row.personId,
        !selectedPersonIds.includes(row.personId),
      );
      return;
    }

    push({
      pathname: memberPathname,
      params: {
        personId: row.personId,
        teamId,
      },
    });
  };
  const onPressStartSelection = () => {
    setPersonSelectionMode(true);
  };
  const onPressInvite = () => {
    navigate({
      pathname: '/team/[teamId]/person/add',
      params: {
        memberType: peopleFilter === 'all' ? 'participant' : peopleFilter,
        teamId,
      },
    });
  };
  const onPressEndSelection = () => {
    clearSelectedPeople();
    setPersonSelectionMode(false);
  };
  const onOpenFilter = () => {
    paginated.resetPage();
    onPressEndSelection();
  };
  const onPressCancelSearch = () => {
    paginated.resetPage();
    onPressEndSelection();
    reset();
  };
  const onChangeSearchText = (nextValue: string) => {
    paginated.resetPage();
    onPressEndSelection();
    onChangeText(nextValue);
  };
  return (
    <Box flex>
      <Section>
        <PeopleToolbar
          actions={
            <>
              {canInvite && !isPersonSelectionMode ? (
                <ActionButton
                  forceLabel
                  icon="person-add-outline"
                  label="Add People"
                  testID="TeamDetailAddMembersButton"
                  variant="success"
                  onPress={onPressInvite}
                />
              ) : null}
              {canAssign ? (
                <TeamMembersActions
                  activeFilterCount={selectedBadgeIds.length}
                  badgeIds={selectedBadgeIds}
                  filterKey={filterKey}
                  isSelectionMode={isPersonSelectionMode}
                  memberType={peopleFilter}
                  peopleCount={visiblePersonIds.length}
                  onOpenFilter={onOpenFilter}
                  selectedPersonIds={selectedPersonIds}
                  onPressClearSelection={onPressEndSelection}
                  onPressEndSelection={onPressEndSelection}
                  onPressStartSelection={onPressStartSelection}
                  teamId={teamId}
                />
              ) : (
                <OrganizationMemberFilterMenu
                  activeFilterCount={
                    selectedBadgeIds.length + (peopleFilter === 'all' ? 0 : 1)
                  }
                  badgeIds={selectedBadgeIds}
                  includeMemberTypes
                  memberType={peopleFilter}
                  targetKey={filterKey}
                  teamId={teamId}
                  testIDPrefix="TeamMembers"
                  onOpenFilter={onOpenFilter}
                />
              )}
            </>
          }
        />
        <Box direction="vertical" space="md" px="lg">
          <SearchBar
            value={value}
            onChangeText={onChangeSearchText}
            onPressCancel={onPressCancelSearch}
            placeholder={`Search ${peopleLabel}...`}
          />
        </Box>
      </Section>
      <Box flex testID="TeamMembersList">
        <PeopleList
          {...scrollInsetProps}
          ref={listRef}
          data={rows}
          fetching={fetching}
          extraData={[selectedPersonIds, isPersonSelectionMode, selectedBadge]}
          keyExtractor={(row) => row.personId}
          keyboardScroll={false}
          ListEmptyComponent={
            <Placeholder
              icon="people-outline"
              text={
                value.trim() ||
                peopleFilter !== 'all' ||
                selectedBadgeIds.length
                  ? 'No people match these filters'
                  : 'No people to show'
              }
            />
          }
          ListFooterComponent={
            peopleCount > paginated.limit ? (
              <Paginator {...paginated} total={peopleCount} />
            ) : null
          }
          renderItem={({ item: row }) => {
            const actions = getTeamMemberSettingsActions({
              isManaged: row.isManaged,
              isSelf: row.personId === userId,
              role: row.role,
              permissions: team?.permissions,
            });
            const canEdit = Object.values(actions).some(Boolean);
            const selected = selectedPersonIds.includes(row.personId);
            return (
              <PeopleListItem
                avatarId={row.personAvatarId ?? ''}
                color={row.personColor ?? ''}
                name={row.personName}
                personTestID={`TeamMembersPerson-${row.personId}`}
                testID="TeamPersonListItem"
                selected={selected}
                onPress={() => onPressRow(row)}
                selection={
                  canAssign && isPersonSelectionMode ? (
                    <Checkbox
                      size="sm"
                      variant="info"
                      accessibilityLabel={`Select ${row.personName}`}
                      value={selected}
                      onValueChange={(checked) =>
                        toggleSelectedPerson(row.personId, checked)
                      }
                      style={styles.selectPerson}
                      testID={`TeamMembersSelectPerson-${row.personId}`}
                    />
                  ) : null
                }
                actions={
                  !isPersonSelectionMode && canEdit ? (
                    <Box alignSelf="center">
                      <PeopleRowActions
                        name={row.personName}
                        size="lg"
                        onEdit={() =>
                          push({
                            pathname: '/team/[teamId]/member/[personId]/edit',
                            params: { teamId, personId: row.personId },
                          })
                        }
                      />
                    </Box>
                  ) : null
                }
                metadata={
                  <Box space="xs">
                    <Text
                      size="sm"
                      variant="typographySecondary"
                      accessibilityLabel={
                        row.memberType === 'participant'
                          ? 'Participating'
                          : 'Not participating'
                      }
                      testID={`TeamMembersParticipating-${row.personId}`}
                    >
                      {row.memberTypeLabel}
                    </Text>
                    {row.roleLabel ? (
                      <Text
                        size="sm"
                        variant="typographySecondary"
                        testID={`TeamMembersRole-${row.personId}`}
                      >
                        {row.roleLabel.replaceAll(' · ', '\n')}
                      </Text>
                    ) : null}
                  </Box>
                }
                details={
                  selectedBadgeAttributes.length ? (
                    <Box style={styles.attributes}>
                      {selectedBadgeAttributes.map((attribute) => (
                        <Box key={attribute.id} style={styles.attribute}>
                          <Text size="xs" variant="typographySecondary">
                            {attribute.title ?? 'Attribute'}
                          </Text>
                          <Text
                            size="sm"
                            testID={`TeamMembersAttribute-${row.personId}-${attribute.id}`}
                          >
                            {getBadgeAttributeValue(
                              row[
                                getOrganizationMemberAttributeColumnId(
                                  attribute.id,
                                )
                              ],
                            ) ?? '—'}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  ) : row.badgePreviews.length ? (
                    <Box
                      direction="horizontal"
                      wrap
                      alignItems="flex-start"
                      space="sm"
                      mt="sm"
                      accessibilityLabel={row.badges}
                      testID={`TeamMembersBadges-${row.personId}`}
                    >
                      {row.badgePreviews.map((badge) => (
                        <Badge
                          key={badge.id}
                          color={badge.color}
                          maxWidth="100%"
                        >
                          <Badge.Text
                            numberOfLines={0}
                            style={styles.badgeText}
                          >
                            {badge.title ?? 'Badge'}
                          </Badge.Text>
                        </Badge>
                      ))}
                    </Box>
                  ) : null
                }
              />
            );
          }}
        />
      </Box>
    </Box>
  );
}

function TeamMembersActions({
  activeFilterCount,
  badgeIds,
  filterKey,
  isSelectionMode,
  peopleCount,
  onOpenFilter,
  onPressClearSelection,
  onPressEndSelection,
  onPressStartSelection,
  selectedPersonIds,
  teamId,
  memberType,
}: {
  activeFilterCount: number;
  badgeIds: string[];
  filterKey: string;
  isSelectionMode: boolean;
  peopleCount: number;
  onOpenFilter: () => void;
  onPressClearSelection: () => void;
  onPressEndSelection: () => void;
  onPressStartSelection: () => void;
  selectedPersonIds: string[];
  teamId: string;
  memberType: PeopleFilter;
}) {
  if (selectedPersonIds.length) {
    return (
      <SelectedTeamMemberActions
        selectedPersonIds={selectedPersonIds}
        teamId={teamId}
        onPressClearSelection={onPressClearSelection}
      />
    );
  }

  if (isSelectionMode) {
    return (
      <ActionButton
        forceLabel
        icon="close-outline"
        label="Done"
        variant="secondary"
        testID="TeamMembersDoneSelectionButton"
        onPress={onPressEndSelection}
      />
    );
  }

  return (
    <>
      <ActionButton
        disabled={!peopleCount}
        forceLabel
        icon="checkbox-outline"
        label="Select"
        variant="secondary"
        testID="TeamMembersSelectPeopleButton"
        onPress={onPressStartSelection}
      />
      <OrganizationMemberFilterMenu
        activeFilterCount={activeFilterCount + (memberType === 'all' ? 0 : 1)}
        badgeIds={badgeIds}
        includeMemberTypes
        memberType={memberType}
        targetKey={filterKey}
        teamId={teamId}
        testIDPrefix="TeamMembers"
        onOpenFilter={onOpenFilter}
      />
    </>
  );
}

function SelectedTeamMemberActions({
  selectedPersonIds,
  teamId,
  onPressClearSelection,
}: {
  selectedPersonIds: string[];
  teamId: string;
  onPressClearSelection: () => void;
}) {
  if (!selectedPersonIds.length) {
    return null;
  }

  return (
    <>
      <ActionButton
        forceLabel
        icon="close-outline"
        label="Clear"
        variant="secondary"
        testID="TeamMembersClearSelectionButton"
        onPress={onPressClearSelection}
      />
      <OrganizationMemberBadgeAssignmentMenu
        selectedPersonIds={selectedPersonIds}
        teamId={teamId}
        testIDPrefix="TeamMembers"
      />
    </>
  );
}

function getBadgeAttributeValue(value: unknown) {
  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return null;
}

const styles = StyleSheet.create((theme) => ({
  selectPerson: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    minHeight: theme.size(11),
    width: theme.size(11),
  },
  attributes: {
    gap: theme.space('md'),
    marginTop: theme.space('sm'),
  },
  attribute: {
    minWidth: theme.size(24),
    maxWidth: '100%',
    flexShrink: 1,
  },
  badgeText: {
    flexShrink: 1,
  },
}));
